import React, { useState, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Play, Square, Loader2, Code2, FileCode, Save } from 'lucide-react';
import { api } from '../../lib/api';

interface CodeExecutionEditorProps {
  serverId: string;
  serverName: string;
}

interface ExecutionResult {
  session_id: string;
  status: 'success' | 'error' | 'timeout';
  output: string;
  error?: string;
  execution_time_ms: number;
  metrics?: {
    cpu_time_ms: number;
    memory_peak_mb: number;
    tool_call_count: number;
  };
}

export function CodeExecutionEditor({ serverId, serverName }: CodeExecutionEditorProps) {
  const [code, setCode] = useState<string>(`// Example: Call MCP tools using code
import * as ${serverName.replace(/-/g, '_')} from './servers/${serverName}';

async function main() {
  try {
    // Call a tool
    const result = await ${serverName.replace(/-/g, '_')}.someMethod({
      // parameters here
    });
    
    console.log('Result:', result);
  } catch (error) {
    console.error('Error:', error);
  }
}

main();
`);
  const [output, setOutput] = useState<string>('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [activeTab, setActiveTab] = useState<string>('editor');
  const editorRef = useRef<any>(null);

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  };

  const executeCode = async () => {
    setIsExecuting(true);
    setOutput('');
    setExecutionResult(null);
    
    try {
      const response = await api.post('/api/code-execution/execute', {
        code,
        server_id: serverId,
        timeout_seconds: 30,
      });

      const result: ExecutionResult = response.data;
      setExecutionResult(result);
      setOutput(result.output || '');
      
      if (result.error) {
        setOutput((prev) => prev + '\n\n' + 'Error: ' + result.error);
      }
      
      setActiveTab('output');
    } catch (error: any) {
      setOutput(`Execution failed: ${error.message}`);
      setActiveTab('output');
    } finally {
      setIsExecuting(false);
    }
  };

  const saveAsSkill = async () => {
    const name = prompt('Enter skill name:');
    if (!name) return;

    const description = prompt('Enter skill description:');
    if (!description) return;

    try {
      await api.post('/api/code-execution/skills', {
        name,
        description,
        code,
        tags: [serverName],
      });
      
      alert('Skill saved successfully!');
    } catch (error: any) {
      alert(`Failed to save skill: ${error.message}`);
    }
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Code2 className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold">Code Execution</h3>
          <span className="text-sm text-gray-500">Server: {serverName}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={saveAsSkill}
            disabled={isExecuting || !code.trim()}
          >
            <Save className="h-4 w-4 mr-1" />
            Save as Skill
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={executeCode}
            disabled={isExecuting || !code.trim()}
          >
            {isExecuting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Executing...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-1" />
                Execute
              </>
            )}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="editor">
            <Code2 className="h-4 w-4 mr-2" />
            Editor
          </TabsTrigger>
          <TabsTrigger value="output">
            <FileCode className="h-4 w-4 mr-2" />
            Output
          </TabsTrigger>
          <TabsTrigger value="tools">
            Tools
          </TabsTrigger>
        </TabsList>

        <TabsContent value="editor" className="flex-1 mt-4">
          <Card className="h-[600px] overflow-hidden">
            <Editor
              height="100%"
              defaultLanguage="typescript"
              value={code}
              onChange={(value) => setCode(value || '')}
              onMount={handleEditorDidMount}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2,
              }}
            />
          </Card>
        </TabsContent>

        <TabsContent value="output" className="flex-1 mt-4">
          <Card className="h-[600px] p-4 overflow-auto">
            {executionResult && (
              <div className="mb-4 p-3 bg-gray-100 rounded-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      executionResult.status === 'success' ? 'bg-green-100 text-green-800' :
                      executionResult.status === 'error' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {executionResult.status.toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-600">
                      {executionResult.execution_time_ms}ms
                    </span>
                  </div>
                  {executionResult.metrics && (
                    <div className="flex items-center space-x-3 text-xs text-gray-600">
                      <span>CPU: {executionResult.metrics.cpu_time_ms}ms</span>
                      <span>Memory: {executionResult.metrics.memory_peak_mb}MB</span>
                      <span>Tools: {executionResult.metrics.tool_call_count}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <pre className="text-sm font-mono whitespace-pre-wrap">
              {output || 'No output yet. Click "Execute" to run your code.'}
            </pre>
          </Card>
        </TabsContent>

        <TabsContent value="tools" className="flex-1 mt-4">
          <ToolsExplorer serverId={serverId} serverName={serverName} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface ToolsExplorerProps {
  serverId: string;
  serverName: string;
}

function ToolsExplorer({ serverId, serverName }: ToolsExplorerProps) {
  const [tools, setTools] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTools();
  }, [serverId]);

  const loadTools = async () => {
    try {
      const response = await api.get(`/api/code-execution/tools?server_id=${serverId}`);
      setTools(response.data.tools || []);
    } catch (error) {
      console.error('Failed to load tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTools = tools.filter(tool =>
    tool.tool_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <Card className="h-[600px] p-4 overflow-auto">
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search tools..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="space-y-3">
        {filteredTools.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No tools found. Make sure the server has tools configured.
          </p>
        ) : (
          filteredTools.map((tool, index) => (
            <div key={index} className="p-3 border rounded-md hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <code className="text-sm font-semibold text-blue-600">
                    {tool.tool_name}
                  </code>
                  <p className="text-sm text-gray-600 mt-1">{tool.description}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`await ${serverName.replace(/-/g, '_')}.${tool.tool_name}({ })`);
                      alert('Code snippet copied!');
                    }}
                    className="text-xs text-blue-500 hover:text-blue-700 mt-2"
                  >
                    Copy code snippet
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}

